import { FormikDatePicker, useNotifications } from '@armhr/ui';
import { Download } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  AlertTitle,
  Box,
  Card,
  CardContent,
  FormHelperText,
  Grow,
  LinearProgress,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { ErrorMessage, Form, Formik } from 'formik';
import { useState } from 'react';
import { Helmet } from 'react-helmet';
import * as Yup from 'yup';

import { useApi } from '../../hooks/useApi';

const validationSchema = Yup.object().shape({
  pay_date_start: Yup.string().required(
    'Starting pay date for the pay period is required'
  ),
});

const BulkVoucherDownloadPage: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [failures, setFailures] = useState<Record<string, string[]>>();
  const api = useApi();
  const { showNotification } = useNotifications();

  const handleSubmit = async (values: {
    pay_date_start: string;
    pay_date_end: string | null;
  }) => {
    try {
      setLoading(true);
      setFailures(undefined);
      const { results } = await api.payroll.getBulkPayrollPdf(
        values.pay_date_start,
        values.pay_date_end
      );
      if (results.failures) {
        setFailures(results.failures);
      }
      if (!results.presigned_url) {
        showNotification({
          message:
            'No PDF vouchers available for that pay period or bulk PDF was empty due to failures.',
          severity: 'info',
        });
        setLoading(false);
        return;
      }
      const link = document.createElement('a');
      link.href = results.presigned_url;
      link.click();
      showNotification({
        message: 'PDF vouchers successfully compiled.',
        severity: 'success',
        autoHideDuration: 5000,
      });
    } catch (err) {
      showNotification({
        message:
          'There was a problem downloading the voucher PDF. Please try again later.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Helmet>
        <title>Bulk Voucher Download | Armhr</title>
        <meta
          name="description"
          content="Download all employee vouchers for a specific pay date."
        />
      </Helmet>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h2">Bulk Voucher Download</Typography>
          <Typography variant="body1">
            Download all employee vouchers for a specific pay period.
          </Typography>
        </Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Some vouchers may not be available for download through this method
            due to data limitations.
          </Typography>
        </Alert>
      </Box>
      <Box>
        <Card>
          <CardContent>
            <Formik
              validateOnMount
              validateOnBlur
              validationSchema={validationSchema}
              initialValues={{
                pay_date_start: '',
                pay_date_end: format(new Date(), 'yyyy-MM-dd'),
              }}
              onSubmit={handleSubmit}
            >
              {({ errors }) => {
                return (
                  <Form>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        width: '100%',
                        alignItems: 'flex-start',
                        gap: 5,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 3,
                        }}
                      >
                        <Box
                          sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}
                        >
                          <Box>
                            <FormikDatePicker
                              name="pay_date_start"
                              label="Pay Date Start"
                              error={!!errors.pay_date_start}
                            />
                            <FormHelperText sx={{ color: 'error.main' }}>
                              <ErrorMessage name="pay_date_start" />
                            </FormHelperText>
                          </Box>
                          <FormikDatePicker
                            name="pay_date_end"
                            label="Pay Date End"
                          />
                        </Box>

                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                            width: '100%',
                          }}
                        >
                          <LoadingButton
                            type="submit"
                            variant="contained"
                            disabled={loading}
                            startIcon={<Download />}
                            sx={{ height: '50%' }}
                          >
                            Download all vouchers
                          </LoadingButton>
                          {loading && (
                            <Grow in={loading}>
                              <Box>
                                <Alert severity="info" sx={{ mb: 2 }}>
                                  <Typography variant="body2">
                                    The download may take a bit of time. Please
                                    be do not leave the page or navigate away
                                    while the PDF is being compiled.
                                  </Typography>
                                </Alert>
                                <LinearProgress
                                  variant="indeterminate"
                                  sx={{
                                    height: 4,
                                    borderRadius: 1,
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{
                                    mt: 1,
                                    display: 'block',
                                    color: 'text.secondary',
                                  }}
                                >
                                  Compiling PDF...
                                </Typography>
                              </Box>
                            </Grow>
                          )}
                        </Box>
                      </Box>
                      {failures && Object.keys(failures).length > 0 && (
                        <Box sx={{ flex: 1 }}>
                          <Alert severity="error">
                            <AlertTitle>Failed Vouchers</AlertTitle>
                            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                              {Object.entries(failures).map(([key, messages]) =>
                                messages.map((message, idx) => (
                                  <Typography
                                    key={`${key}-${idx}`}
                                    component="li"
                                    variant="body2"
                                    sx={{ mb: 0.5 }}
                                  >
                                    {message}
                                  </Typography>
                                ))
                              )}
                            </Box>
                          </Alert>
                        </Box>
                      )}
                    </Box>
                  </Form>
                );
              }}
            </Formik>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default BulkVoucherDownloadPage;
